import { useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createTask, notifyTasksChanged } from "@/lib/tasks";
import { cn } from "@/lib/utils";

/**
 * QuickAddTask – skriv og trykk Enter. Ein-hands mobilflyt: nytt punkt på under 10 sekund.
 * defaults: {machine_id, customer_id} blir lagt på punktet. onMore(title) opnar full dialog med tittelen.
 */
export const QuickAddTask = ({ defaults, onCreated, onMore, placeholder = "Legg til noko som må hugsast …", className, testid = "task-quick-add", autoFocus = false }) => {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const task = await createTask({ title: t, priority: "normal", ...(defaults || {}) });
      setTitle("");
      notifyTasksChanged();
      onCreated?.(task);
      toast.success("Lagt på huskelista.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje leggje til.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className={cn("flex items-center gap-2", className)} data-testid={testid}>
      <div className="relative min-w-0 flex-1">
        <Plus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={placeholder}
          maxLength={200}
          autoFocus={autoFocus}
          className="h-12 pl-9 pr-3 text-[15px]"
          data-testid={`${testid}-input`}
          aria-label="Nytt punkt på huskelista"
        />
      </div>
      {onMore && (
        <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => onMore(title.trim())} aria-label="Fleire val" data-testid={`${testid}-more`}>
          <SlidersHorizontal className="h-5 w-5" />
        </Button>
      )}
      <Button type="submit" className="h-12 shrink-0 px-4" disabled={busy || !title.trim()} data-testid={`${testid}-submit`}>
        {busy ? <Loader2 className="animate-spin" /> : <Plus />} <span className="hidden sm:inline">Legg til</span>
      </Button>
    </form>
  );
};
